import React from "react";
import { FiMessageSquare } from "react-icons/fi";

const formatTimestamp = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const now = Date.now();
  const diffMs = now - date.getTime();
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;

  if (diffMs < minuteMs) {
    return "Just now";
  }
  if (diffMs < hourMs) {
    const minutes = Math.floor(diffMs / minuteMs);
    return `${minutes}m ago`;
  }
  if (diffMs < dayMs) {
    const hours = Math.floor(diffMs / hourMs);
    return `${hours}h ago`;
  }
  if (diffMs < 7 * dayMs) {
    const days = Math.floor(diffMs / dayMs);
    return `${days}d ago`;
  }

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

const ChatHistoryCard = ({
  entry = null,
  title,
  preview,
  timestamp,
  messageCount,
  onSelect,
  onClick,
  className = "",
}) => {
  const normalizedEntry =
    entry || {
      title,
      preview,
      updatedAt: timestamp,
      messageCount,
    } ||
    null;

  const cardTitle = normalizedEntry?.title ?? title;
  const cardPreview = normalizedEntry?.preview ?? preview;
  const cardTimestamp = normalizedEntry?.updatedAt ?? timestamp;
  const cardMessageCount = normalizedEntry?.messageCount ?? messageCount;

  const formattedTimestamp = formatTimestamp(cardTimestamp);
  const normalizedPreview =
    typeof cardPreview === "string" && cardPreview.trim().length > 0
      ? cardPreview.trim()
      : "";
  const normalizedTitle =
    typeof cardTitle === "string" && cardTitle.trim().length > 0
      ? cardTitle.trim()
      : "Untitled chat";
  const normalizedMessageCount =
    Number.isFinite(cardMessageCount) && cardMessageCount > 0
      ? cardMessageCount
      : null;

  const handleClick = () => {
    if (typeof onSelect === "function") {
      onSelect(normalizedEntry);
      return;
    }
    if (typeof onClick === "function") {
      onClick(normalizedEntry);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`group flex mt-2 w-full items-center gap-4 rounded-xl border border-gray-200 bg-white px-4 py-3 text-left
    shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_1px_2px_rgba(16,24,40,0.08),0_6px_14px_-6px_rgba(16,24,40,0.18)]
    transition-all duration-150 hover:border-[#0D99FF]/30 hover:bg-[#0D99FF]/5
    hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_4px_10px_rgba(16,24,40,0.10),0_16px_32px_-12px_rgba(117,97,243,0.25)]
    active:translate-y-[1px] active:shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_1px_2px_rgba(16,24,40,0.16)]
    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0D99FF] ${className}`}
      aria-label={`Open chat history for ${normalizedTitle}`}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-gray-900">
          {normalizedTitle}
        </p>
        {normalizedPreview && (
          <p className="mt-1 text-xs text-gray-600">{normalizedPreview}</p>
        )}
        <div className="mt-2 flex items-center gap-3 text-[11px] font-medium uppercase tracking-wide text-gray-400">
          {formattedTimestamp && <span>{formattedTimestamp}</span>}
          {normalizedMessageCount && (
            <span className="flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 group-hover:bg-[#0D99FF]/10 group-hover:text-[#0D99FF]">
              <FiMessageSquare className="h-3 w-3" aria-hidden="true" />
              {normalizedMessageCount}{" "}
              {normalizedMessageCount === 1 ? "message" : "messages"}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

export default ChatHistoryCard;
